/**
 * Cliente de API para el módulo SaaS: Planes, límites por tenant y superadministrador.
 */

import { apiRequest } from "./api";

export interface SaasPlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price_monthly_cents: number;
  max_branches: number;
  max_users: number;
  max_products: number;
  features: Record<string, boolean | string | number> | null;
  is_active: boolean;
}

export interface ResourceUsage {
  branches_used: number;
  branches_limit: number;
  users_used: number;
  users_limit: number;
  products_used: number;
  products_limit: number;
}

export interface MySubscription {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  plan: SaasPlan;
  status: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  usage: ResourceUsage;
}

export interface SaasTenantItem {
  id: string;
  slug: string;
  name: string;
  rfc: string | null;
  is_active: boolean;
  created_at: string;
  plan_code: string;
  plan_name: string;
  subscription_status: string;
  users_count: number;
  max_users: number;
  branches_count: number;
  max_branches: number;
  products_count: number;
  max_products: number;
  has_pos?: boolean;
  has_restaurant?: boolean;
  has_workshop?: boolean;
  schema_mode?: "shared" | "dedicated";
  schema_name?: string | null;
}

export interface SaasMetrics {
  total_tenants: number;
  active_tenants: number;
  trial_tenants: number;
  suspended_tenants: number;
  total_mrr_cents: number;
  total_users: number;
}

export interface OnboardingRequest {
  slug: string;
  name: string;
  owner_email: string;
  owner_name: string;
  owner_password: string;
  plan_code?: string;
  branch_name?: string;
  branch_code?: string;
  rfc?: string | null;
  has_pos?: boolean;
  has_restaurant?: boolean;
  has_workshop?: boolean;
  inquiry_id?: string | null;
}

export interface OnboardingResponse {
  tenant_id: string;
  slug: string;
  name: string;
  branch_id: string;
  owner_id: string;
  plan_code: string;
  message: string;
  welcome_email_queued?: boolean;
}

export interface ImpersonateResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  refresh_expires_at?: string;
  target_tenant_id?: string;
  target_tenant_slug: string;
  target_tenant_name?: string;
  target_email: string;
  impersonated_by_user_id: string;
}

const BASE = "/api/v1/saas";

export function listPlans(): Promise<SaasPlan[]> {
  return apiRequest(`${BASE}/plans`);
}

export function getMySubscription(): Promise<MySubscription> {
  return apiRequest(`${BASE}/my-subscription`);
}

export function getSaasMetrics(): Promise<SaasMetrics> {
  return apiRequest(`${BASE}/admin/metrics`);
}

export function listSaasTenants(): Promise<SaasTenantItem[]> {
  return apiRequest(`${BASE}/admin/tenants`);
}

export function onboardTenant(payload: OnboardingRequest): Promise<OnboardingResponse> {
  return apiRequest(`${BASE}/admin/tenants/onboard`, {
    method: "POST",
    body: payload,
  });
}

export function updateTenantStatus(
  tenantId: string,
  isActive: boolean,
  reason?: string
): Promise<{ tenant_id: string; is_active: boolean; message: string }> {
  return apiRequest(`${BASE}/admin/tenants/${tenantId}/status`, {
    method: "POST",
    body: { is_active: isActive, reason },
  });
}

export function changeTenantPlan(
  tenantId: string,
  planCode: string
): Promise<{ tenant_id: string; plan_id: string; message: string }> {
  return apiRequest(`${BASE}/admin/tenants/${tenantId}/plan`, {
    method: "POST",
    body: { plan_code: planCode },
  });
}

export function impersonateTenant(tenantId: string): Promise<ImpersonateResponse> {
  return apiRequest(`${BASE}/admin/tenants/${tenantId}/impersonate`, {
    method: "POST",
  });
}

export function updateTenantServices(
  tenantId: string,
  services: { has_pos: boolean; has_restaurant: boolean; has_workshop: boolean }
): Promise<{
  tenant_id: string;
  has_pos: boolean;
  has_restaurant: boolean;
  has_workshop: boolean;
  message: string;
}> {
  return apiRequest(`${BASE}/admin/tenants/${tenantId}/services`, {
    method: "PATCH",
    body: services,
  });
}

export function upgradeTenantSchema(tenantId: string): Promise<{
  tenant_id: string;
  schema_name: string;
  schema_mode: string;
  message: string;
}> {
  return apiRequest(`${BASE}/admin/tenants/${tenantId}/upgrade-schema`, {
    method: "POST",
  });
}

